import { generate, ident, parse, walk } from "css-tree";
import type { Doc, Layer } from "@hightouch/lib/ad-studio/html-editor/model";

/** Scope only the displayed copy: SVG fragment IDs are global even across separate ad roots. */
export function scopeSvgIds(root: Element, prefix: string): void {
  const ids = collectSvgIds([root], prefix);
  if (ids.size) rewriteTree(root, ids);
}

export interface ScopedSvgDoc {
  doc: Doc;
  ids: ReadonlyMap<string, string>;
}

export function scopeDocSvgIds(doc: Doc, prefix: string): Doc {
  return createScopedSvgDoc(doc, prefix).doc;
}

/** Rewrite a rich-text display copy, or restore original references with an inverse map. */
export function rewriteSvgReferences(
  html: string,
  ids: ReadonlyMap<string, string>,
): string {
  if (!ids.size || !html.includes("#")) return html;
  const body = new DOMParser().parseFromString(html, "text/html").body;
  rewriteTree(body, ids);
  return body.innerHTML;
}

/** Leave layer identities and the saved Doc untouched; only its rendering copy gets scoped references. */
export function createScopedSvgDoc(
  doc: Doc,
  prefix: string,
  knownIds?: Map<string, string>,
): ScopedSvgDoc {
  const fragments = new Map<Layer, HTMLElement>();
  const svgLayers: Extract<Layer, { type: "svg" }>[] = [];
  function parseFragment(layer: Extract<Layer, { type: "svg" }>) {
    return new DOMParser().parseFromString(
      `<svg>${layer.innerMarkup}</svg>`,
      "text/html",
    ).body;
  }
  function collect(layers: readonly Layer[]) {
    for (const layer of layers) {
      if (layer.type === "svg") {
        svgLayers.push(layer);
        // Conservative precheck: avoid parsing path-heavy SVGs that define no IDs.
        if (/\bid\s*=/i.test(layer.innerMarkup)) {
          fragments.set(layer, parseFragment(layer));
        }
      }
      if (layer.type === "group") collect(layer.children);
    }
  }
  collect(doc.layers);
  const ids = collectSvgIds([...fragments.values()], prefix, knownIds);
  if (!ids.size) return { doc, ids };
  // ID-free fragments can still reference definitions in another SVG layer.
  for (const layer of svgLayers) {
    const root = fragments.get(layer) ?? parseFragment(layer);
    fragments.set(layer, root);
    rewriteTree(root, ids);
  }

  function rewriteLayer(layer: Layer): Layer {
    const scoped = {
      ...layer,
      ...(layer.extraStyles
        ? {
            extraStyles: Object.fromEntries(
              Object.entries(layer.extraStyles).map(([key, value]) => [
                key,
                rewriteUrls(value, ids),
              ]),
            ),
          }
        : {}),
    };
    if (scoped.type === "svg") {
      scoped.innerMarkup =
        fragments.get(layer)?.firstElementChild?.innerHTML ??
        scoped.innerMarkup;
    }
    if (scoped.type === "text") {
      scoped.content = rewriteSvgReferences(scoped.content, ids);
    }
    if (scoped.type === "group")
      scoped.children = scoped.children.map(rewriteLayer);
    return scoped;
  }
  return {
    ids,
    doc: {
      ...doc,
      background: doc.background ? rewriteUrls(doc.background, ids) : null,
      layers: doc.layers.map(rewriteLayer),
    },
  };
}

function collectSvgIds(
  roots: readonly Element[],
  prefix: string,
  knownIds = new Map<string, string>(),
) {
  const ids = new Map<string, string>();
  for (const root of roots) {
    for (const element of Array.from(
      root.querySelectorAll("svg[id], svg [id]"),
    )) {
      const id = element.id;
      if (!id) continue;
      // Keep live contentEditable references valid when definitions are inserted or reordered.
      if (!knownIds.has(id)) knownIds.set(id, `${prefix}-${knownIds.size}`);
      ids.set(id, knownIds.get(id)!);
    }
  }
  return ids;
}

function rewriteUrls(value: string, ids: ReadonlyMap<string, string>): string {
  return value.replace(
    /url\(\s*(["']?)#([^\s"')]+)\1\s*\)/gi,
    (match, _quote: string, id: string) => {
      const scoped = ids.get(id);
      return scoped ? `url("#${scoped}")` : match;
    },
  );
}

function rewriteTree(root: Element, ids: ReadonlyMap<string, string>): void {
  for (const element of Array.from(root.querySelectorAll("*"))) {
    // Match collection's subtree boundary, including HTML inside foreignObject.
    if (element.closest("svg")) {
      const scoped = ids.get(element.id);
      if (scoped) element.id = scoped;
    }
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name === "id") continue;
      let value = rewriteUrls(attribute.value, ids);
      if (attribute.localName === "href" && value.startsWith("#")) {
        const scoped = ids.get(value.slice(1));
        if (scoped) value = `#${scoped}`;
      }
      if (["aria-labelledby", "aria-describedby"].includes(attribute.name)) {
        value = value.replace(/\S+/g, (id) => ids.get(id) ?? id);
      }
      if (value !== attribute.value) attribute.value = value;
    }
    if (element.tagName.toLowerCase() === "style" && element.textContent) {
      element.textContent = rewriteStyleSheet(element.textContent, ids);
    }
  }
}

function rewriteStyleSheet(
  css: string,
  ids: ReadonlyMap<string, string>,
): string {
  const ast = parse(css, { parseCustomProperty: true });
  let changed = false;
  walk(ast, (node) => {
    if (node.type === "IdSelector") {
      const scoped = ids.get(ident.decode(node.name));
      if (scoped) {
        node.name = ident.encode(scoped);
        changed = true;
      }
    } else if (node.type === "Url" && node.value.startsWith("#")) {
      const scoped = ids.get(node.value.slice(1));
      if (scoped) {
        node.value = `#${scoped}`;
        changed = true;
      }
    }
  });
  return changed ? generate(ast) : css;
}
